import ContentCut from '@mui/icons-material/ContentCut'
import Delete from '@mui/icons-material/Delete'
import Edit from '@mui/icons-material/Edit'
import {
  Alert,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import type { ComponentChildren } from 'preact'
import { useState } from 'preact/hooks'
import type { Assignment } from '../api/generated/Assignment'
import type { MemberRole } from '../api/generated/MemberRole'
import type { Position } from '../api/generated/Position'
import type { Shift } from '../api/generated/Shift'
import type { ShiftFields } from '../api/generated/ShiftFields'
import type { ShiftSeries } from '../api/generated/ShiftSeries'
import type { ShiftSplit } from '../api/generated/ShiftSplit'
import { useAction } from '../api/useAction'
import { useApi } from '../api/useApi'
import { withCounts } from '../assignments/counts'
import { ShiftAssignments } from '../assignments/ShiftAssignments'
import { useEvent } from '../events/EventContext'
import { planningDays } from '../planning/grid'
import { SeriesDialog } from '../planning/SeriesDialog'
import { FillRate } from './FillRate'
import { fillOf } from './fill'
import { ShiftDialog } from './ShiftDialog'
import { SplitDialog } from './SplitDialog'
import { TimezoneNote } from './TimezoneNote'
import { formatRange } from './time'
import { confirmRemoval, shiftWrites } from './writes'

type Dialog = { kind: 'new' | 'series' } | { kind: 'edit' | 'split'; shift: Shift }

export function ShiftAction({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled?: boolean
  onClick: () => void
  children: ComponentChildren
}) {
  // The span keeps the tooltip working while the button is disabled
  return (
    <Tooltip title={label}>
      <span>
        <IconButton size="small" aria-label={label} disabled={disabled} onClick={onClick}>
          {children}
        </IconButton>
      </span>
    </Tooltip>
  )
}

export function ShiftList({
  slug,
  position,
  role,
  timezone,
  openSeries = false,
}: {
  slug: string
  position: Position
  role: MemberRole
  timezone: string
  /** Opens the series of shifts at once, for a position just created. */
  openSeries?: boolean
}) {
  const { event } = useEvent()
  const { data, setData, error } = useApi<Shift[]>(`/events/${slug}/shifts`)
  const assignments = useApi<Assignment[]>(
    role === 'organizer' ? `/events/${slug}/assignments` : null,
  )
  const [dialog, setDialog] = useState<Dialog | undefined>(
    openSeries && role === 'organizer' ? { kind: 'series' } : undefined,
  )
  const removal = useAction()
  const positionId = position.id
  const shifts = data?.filter((shift) => shift.position_id === positionId)
  const days = data ? planningDays(data, event) : []

  const writes = shiftWrites(slug, setData)
  const create = async (fields: ShiftFields) => {
    await writes.create({ position_id: positionId, ...fields })
    setDialog(undefined)
  }
  const update = (shift: Shift) => async (fields: ShiftFields) => {
    await writes.update(shift, fields)
    setDialog(undefined)
  }
  const createSeries = async (series: ShiftSeries) => {
    await writes.createSeries(series)
    setDialog(undefined)
  }
  const split = (shift: Shift) => async (shiftSplit: ShiftSplit) => {
    await writes.split(shift, shiftSplit)
    setDialog(undefined)
  }
  const remove = (shift: Shift) => {
    if (!confirmRemoval(shift)) return
    removal.run(() => writes.remove(shift))
  }
  const changeAssignments = (all: Assignment[]) => {
    assignments.setData(all)
    setData((current) => current && withCounts(current, all))
  }
  const last = shifts?.at(-1)

  return (
    <Stack spacing={1}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Créneaux</Typography>
        {shifts && shifts.length > 0 && <FillRate fill={fillOf(shifts)} />}
      </Stack>
      <TimezoneNote timezone={timezone} />
      {error && <Alert severity="error">{error}</Alert>}
      {assignments.error && <Alert severity="error">{assignments.error}</Alert>}
      {removal.error && <Alert severity="error">{removal.error}</Alert>}
      {shifts?.length === 0 && <Typography color="text.secondary">Aucun créneau.</Typography>}
      {shifts && shifts.length > 0 && (
        <List dense disablePadding>
          {shifts.map((shift) => (
            <ListItem key={shift.id} disableGutters sx={{ gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 180 }}>
                {formatRange(shift.starts_at, shift.ends_at)}
              </Typography>
              <FillRate fill={fillOf([shift])} />
              {role === 'organizer' && (
                <Box>
                  <ShiftAction label="Modifier" onClick={() => setDialog({ kind: 'edit', shift })}>
                    <Edit fontSize="small" />
                  </ShiftAction>
                  <ShiftAction
                    label="Découper en créneaux plus courts"
                    onClick={() => setDialog({ kind: 'split', shift })}
                  >
                    <ContentCut fontSize="small" />
                  </ShiftAction>
                  <ShiftAction
                    label="Supprimer"
                    disabled={removal.pending}
                    onClick={() => remove(shift)}
                  >
                    <Delete fontSize="small" />
                  </ShiftAction>
                </Box>
              )}
              {assignments.data && (
                <ShiftAssignments
                  slug={slug}
                  shiftId={shift.id}
                  assignments={assignments.data.filter((a) => a.shift_id === shift.id)}
                  onChange={changeAssignments}
                />
              )}
            </ListItem>
          ))}
        </List>
      )}
      {role === 'organizer' && (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" disabled={!shifts} onClick={() => setDialog({ kind: 'new' })}>
            Ajouter un créneau
          </Button>
          <Button
            variant="outlined"
            disabled={days.length === 0}
            onClick={() => setDialog({ kind: 'series' })}
          >
            Créer une série
          </Button>
        </Stack>
      )}
      {dialog?.kind === 'series' && days[0] && (
        <SeriesDialog
          positions={[position]}
          days={days}
          initialDay={days[0]}
          onSubmit={createSeries}
          onClose={() => setDialog(undefined)}
        />
      )}
      {dialog?.kind === 'new' && (
        <ShiftDialog
          title="Nouveau créneau"
          initial={{
            starts_at: last?.ends_at ?? '',
            ends_at: '',
            required_volunteers: last?.required_volunteers ?? 1,
          }}
          onSubmit={create}
          onClose={() => setDialog(undefined)}
        />
      )}
      {dialog?.kind === 'edit' && (
        <ShiftDialog
          title="Modifier le créneau"
          initial={dialog.shift}
          onSubmit={update(dialog.shift)}
          onClose={() => setDialog(undefined)}
        />
      )}
      {dialog?.kind === 'split' && (
        <SplitDialog
          shift={dialog.shift}
          onSubmit={split(dialog.shift)}
          onClose={() => setDialog(undefined)}
        />
      )}
    </Stack>
  )
}
